/*
  Projects list — pure helpers (no React), shared by the page, its rows and tests.

  Wave 1 of the overhaul: read mode by default with one ⋯ menu per row, status tabs with counts +
  "Mine", clean rows (no "Client: -" labels, notes as an icon, an automatic "New · 2 d" badge instead of
  the "New project" tag, "Needs setup" for active projects missing basics) and one colour per system.

  Wave 2 (03 #4–#8): the dense table sorted by "Needs attention", filter chips kept in the URL with a
  search over the loaded list, clean cities, the stage / due columns with attention shortcuts, grouping
  the systems of one site, and the validated New / Edit project panel.

  Wave 3 (03 #10): the server stores a clean city / emirate and the map pin of every project
  (backend/project_geo.py); projectCity() reads that city (the list, the City filter, the map's groups).
*/
import { ageDays, formatDays, parseDateValue, toDateInputValue } from '@/lib/format';
import { compareValues } from '@/lib/table';
import { pinFromLink } from './pinLink';

export const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'Active', label: 'Active' },
  { key: 'On Hold', label: 'On hold' },
  { key: 'Completed', label: 'Completed' },
];
export const DEFAULT_STATUS_TAB = 'Active';
export const PROJECT_STATUSES = ['Active', 'On Hold', 'Completed'];

/** A project counts as new (lime "New · 2 d" badge) for this many days after it was created. */
export const NEW_PROJECT_DAYS = 14;
/** The old manual "New project" tag — replaced by the automatic badge, so it is not shown any more. */
const AUTO_TAG_PATTERN = /^new\s+project$/i;
const COORDINATES_PATTERN = /^\s*-?\d{1,3}\.\d+\s*,\s*-?\d{1,3}\.\d+\s*$/;
const URL_PATTERN = /^https?:\/\//i;

export const cleanText = (value) => String(value ?? '').trim();
const normalized = (value) => cleanText(value).toLowerCase();

/*
  A name as two names are compared (like backend/project_name_rule.py): compatibility forms read as plain letters
  (NFKC: full-width "Ｎｏｂｕ" = "Nobu"), invisible format characters dropped (Unicode Cf: zero-width space, word
  joiner, BOM, direction marks), trimmed, runs of spaces as one.
*/
const FORMAT_CHARACTERS = /\p{Cf}/gu;
const nameWords = (value) => String(value ?? '').normalize('NFKC').replace(FORMAT_CHARACTERS, '').trim().replace(/\s+/g, ' ');

export const projectHref = (project) => `/projects/${encodeURIComponent(project?.id || '')}`;

// One colour per system — moved to the foundation (components/ui/system-chip) in the wave 2 integration.
export { SYSTEM_COLORS, getSystemTone } from '@/components/ui/system-chip';

/** Status tab match ('all' matches everything; case-insensitive so legacy spellings still count). */
export function matchesStatusTab(project, tab) {
  if (!tab || tab === 'all') return true;
  return normalized(project?.status) === normalized(tab);
}

/** { all, Active, 'On Hold', Completed } for the tab badges. */
export function countByStatus(projects) {
  const counts = { all: 0 };
  STATUS_TABS.forEach((tab) => { if (tab.key !== 'all') counts[tab.key] = 0; });
  (projects || []).forEach((project) => {
    counts.all += 1;
    const tab = STATUS_TABS.find((item) => item.key !== 'all' && matchesStatusTab(project, item.key));
    if (tab) counts[tab.key] += 1;
  });
  return counts;
}

/** A person name as typed into project fields, compared ignoring case, "_" and extra spaces. */
const personKey = (value) => cleanText(value).toLowerCase().replace(/_/g, ' ').split(/\s+/).filter(Boolean).join(' ');

/**
 * The ways a user's name is typed into the engineer fields — the same rule as the dashboard's "Mine"
 * (backend dashboard_summary.person_keys): the user's name and the name part of their email
 * (ali.eyad@… → "ali eyad").
 */
export function personKeys(user) {
  const keys = new Set();
  const name = personKey(user?.name);
  if (name) keys.add(name);
  const email = cleanText(user?.email).toLowerCase();
  if (email.includes('@')) {
    const local = email.split('@', 1)[0].split(/[._\-+\s]+/).filter(Boolean).join(' ');
    if (local) keys.add(local);
  }
  return [...keys];
}

/** "Mine": I am the Project Engineer or the Senior Project Engineer. `me` = a name or personKeys(user). */
export function isMyProject(project, me) {
  const keys = (Array.isArray(me) ? me : [me]).map(personKey).filter(Boolean);
  if (!keys.length) return false;
  return keys.includes(personKey(project?.project_engineer)) || keys.includes(personKey(project?.senior_project_engineer));
}

// "…, UAE." / "Abu Dhabi-UAE": the country adds nothing on the row (every site is in the UAE or Riyadh).
const COUNTRY_SUFFIX = /[\s,.\-–]*\b(u\.?a\.?e\.?|united arab emirates)[\s.]*$/i;
const EDGE_SEPARATORS = /^[\s,.\-–·]+|[\s,.\-–·]+$/g;

/**
 * Location for the row: as written without the country and stray punctuation ('Al Nahyan, Abu Dhabi, UAE.' →
 * 'Al Nahyan, Abu Dhabi'); raw GPS coordinates / map links read as "Pin location". `title` keeps the text as typed.
 */
export function describeLocation(location) {
  const text = cleanText(location);
  if (!text) return null;
  if (COORDINATES_PATTERN.test(text) || URL_PATTERN.test(text)) return { pin: true, text: 'Pin location', title: text };
  const shown = text.replace(COUNTRY_SUFFIX, '').replace(EDGE_SEPARATORS, '').trim();
  return { pin: false, text: shown || text, title: text };
}

/** Tags worth showing on the row (the old "New project" tag is replaced by the automatic badge). */
export function visibleTags(tags) {
  if (!Array.isArray(tags)) return [];
  return [...new Set(tags.map(cleanText).filter((tag) => tag && !AUTO_TAG_PATTERN.test(tag)))];
}

export function scopeText(scope) {
  return Array.isArray(scope) ? scope.map(cleanText).filter(Boolean).join(', ') : cleanText(scope);
}

/** 'New · 2 d' for the first NEW_PROJECT_DAYS days after created_at, else null. */
export function newProjectLabel(project, { now } = {}) {
  const days = ageDays(project?.created_at, { now });
  if (days === null || days < 0 || days >= NEW_PROJECT_DAYS) return null;
  return `New · ${days === 0 ? 'today' : formatDays(days)}`;
}

// A city the server found from the location or the map pin settles "Needs setup: city"; one guessed from the
// project name ("Yas Riva", "Civil Defence - Al Dhafra") is shown and filtered on, but someone still has to say
// where the site is (03 #10, wave 3 review).
const SETUP_CITY_SOURCES = new Set(['location', 'pin']);

/**
 * What an ACTIVE project still needs before people can work with it: ['client', 'city', 'engineer'].
 * The city counts as known when the location is typed or the server read it from the location / map pin.
 */
export function missingSetup(project) {
  if (!matchesStatusTab(project, 'Active')) return [];
  const missing = [];
  if (!cleanText(project?.client)) missing.push('client');
  const cityKnown = cleanText(project?.location)
    || (cleanText(project?.city) && SETUP_CITY_SOURCES.has(cleanText(project?.city_source)));
  if (!cityKnown) missing.push('city');
  if (!cleanText(project?.project_engineer)) missing.push('engineer');
  return missing;
}

/** Only http(s) Waze links are opened (the field is free text). */
export function safeExternalUrl(value) {
  const text = cleanText(value);
  return URL_PATTERN.test(text) ? text : '';
}

// Records in other collections that DELETE removes with the project (impact.counts).
const COUNT_LABELS = [
  ['boq_items', 'BOQ item', 'BOQ items'],
  ['lpos', 'LPO', 'LPOs'],
  ['invoices', 'invoice', 'invoices'],
  ['variation_invoices', 'variation invoice', 'variation invoices'],
  ['stock_movements', 'stock movement', 'stock movements'],
  ['reservations', 'stock reservation', 'stock reservations'],
  ['delivery_notes', 'delivery note', 'delivery notes'],
  ['site_requests', 'site request', 'site requests'],
  ['documents', 'document', 'documents'],
  ['variations', 'variation', 'variations'],
];
// Lists stored inside the project document (impact.embedded) — they go with it too.
const EMBEDDED_LABELS = [
  ['execution_tasks', 'execution task', 'execution tasks'],
  ['material_site_needs', 'material site need', 'material site needs'],
  ['payment_terms', 'payment term', 'payment terms'],
];
// Records DELETE leaves in place, pointing at a project that no longer exists (impact.kept).
const KEPT_LABELS = [
  ['tasks', 'task', 'tasks'],
  ['shop_drawings', 'shop drawing in the register', 'shop drawings in the register'],
];

const countItems = (counts, labels) => labels
  .map(([key, singular, pluralWord]) => {
    const count = Number(counts?.[key]) || 0;
    return { key, count, label: count === 1 ? singular : pluralWord };
  })
  .filter((item) => item.count > 0);

/** The execution structure as one line item: its devices, else its floors, else its buildings. */
function structureItem(structure) {
  const levels = [['devices', 'structure device'], ['floors', 'structure floor'], ['buildings', 'structure building']];
  for (const [key, singular] of levels) {
    const count = Number(structure?.[key]) || 0;
    if (count > 0) return { key: 'structure', count, label: count === 1 ? singular : `${singular}s` };
  }
  return null;
}

/**
 * Everything the delete removes besides the project and its stages, from GET /projects/{id}/delete-impact:
 * [{ key: 'boq_items', count: 12, label: 'BOQ items' }, …, { key: 'execution_tasks', count: 2460, … }]
 * — linked records first, then what is stored inside the project; only what exists, in a fixed order.
 */
export function deleteImpactItems(impact) {
  const execution = countItems(impact?.embedded, EMBEDDED_LABELS);
  const structure = structureItem(impact?.structure);
  return [
    ...countItems(impact?.counts, COUNT_LABELS),
    ...execution.filter((item) => item.key === 'execution_tasks'),
    ...(structure ? [structure] : []),
    ...execution.filter((item) => item.key !== 'execution_tasks'),
  ];
}

/** What the delete leaves behind without a project (tasks linked to it, Shop Drawings rows naming it). */
export function deleteKeptItems(impact) {
  return countItems(impact?.kept, KEPT_LABELS);
}

/** The confirmation's opening sentence — "Nothing else is linked" only when nothing at all is. */
export function deleteImpactDescription({ stages = 0, removed = [], kept = [] }) {
  const stagesText = stages > 0 ? plural(stages, 'delivery stage') : '';
  if (removed.length) {
    return `This permanently removes the project${stagesText ? `, its ${stagesText}` : ''} and everything stored with it:`;
  }
  const base = `This permanently removes the project${stagesText ? ` and its ${stagesText}` : ''}.`;
  return kept.length ? base : `${base} Nothing else is linked to it.`;
}

/** Text the delete confirmation asks for: the name with runs of spaces collapsed (as it is displayed). */
export const deleteConfirmName = (name) => cleanText(name).replace(/\s+/g, ' ') || 'Untitled project';

export const plural = (count, singular, pluralWord = `${singular}s`) => `${count} ${count === 1 ? singular : pluralWord}`;

/* ---------- create / edit form ---------- */

export const DEFAULT_SCOPE_OPTIONS = ['Cable Pulling', 'Panels termination', 'installation', 'T&C'];

export const createProjectCustomField = (overrides = {}) => ({
  id: overrides.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  content: '',
  ...overrides,
});

export const normalizeProjectCustomFields = (fields) => (
  Array.isArray(fields)
    ? fields.map((field) => createProjectCustomField({
      id: field.id,
      title: field.title || '',
      content: field.content || '',
    }))
    : []
);

export const EMPTY_PROJECT_FORM = {
  name: '',
  client: '',
  consultant: '',
  contractor: '',
  project_engineer: '',
  senior_project_engineer: '',
  location: '',
  waze_url: '',
  system_type: '',
  scope: [],
  tags: [],
  status: 'Active',
  start_date: '',
  end_date: '',
  notes: '',
  custom_fields: [],
  allow_sub_boq: false,
};

/** The edit form for an existing project. */
export function projectToForm(project) {
  return {
    name: project.name || '',
    client: project.client || '',
    consultant: project.consultant || '',
    contractor: project.contractor || '',
    project_engineer: project.project_engineer || '',
    senior_project_engineer: project.senior_project_engineer || '',
    location: project.location || '',
    waze_url: project.waze_url || '',
    system_type: project.system_type || '',
    scope: Array.isArray(project.scope) ? project.scope : [],
    tags: Array.isArray(project.tags) ? project.tags : [],
    status: project.status || 'Active',
    start_date: project.start_date || '',
    end_date: project.end_date || '',
    notes: project.notes || '',
    custom_fields: normalizeProjectCustomFields(project.custom_fields),
    allow_sub_boq: Boolean(project.allow_sub_boq),
  };
}

/**
 * "Duplicate for another system": same site, client, team, dates and scope — a new system type,
 * a fresh status and no notes / extra fields (those belong to the original system's project).
 */
export function projectToDuplicateForm(project) {
  return {
    ...projectToForm(project),
    system_type: '',
    status: 'Active',
    notes: '',
    custom_fields: [],
  };
}

export function normalizeProjectPayload(form) {
  return {
    ...form,
    name: cleanText(form.name),
    client: cleanText(form.client),
    consultant: cleanText(form.consultant),
    contractor: cleanText(form.contractor),
    project_engineer: cleanText(form.project_engineer),
    senior_project_engineer: cleanText(form.senior_project_engineer),
    location: cleanText(form.location),
    waze_url: cleanText(form.waze_url),
    system_type: cleanText(form.system_type),
    notes: cleanText(form.notes),
    tags: form.tags,
    custom_fields: normalizeProjectCustomFields(form.custom_fields)
      .map((field) => ({ ...field, title: field.title.trim(), content: field.content.trim() }))
      .filter((field) => field.title || field.content),
    // '' (not null) so clearing a date in Edit really clears it (the server ignores null fields).
    start_date: cleanText(form.start_date),
    end_date: cleanText(form.end_date),
    allow_sub_boq: Boolean(form.allow_sub_boq),
  };
}

/* =====================================================================================
   Wave 2 — the list (03 #4–#7)
   ===================================================================================== */

export const PROJECT_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'On Hold', label: 'On hold' },
  { value: 'Completed', label: 'Completed' },
];
const STATUS_ORDER = { active: 0, 'on hold': 1, completed: 2 };
export const statusOrder = (status) => STATUS_ORDER[normalized(status)] ?? 3;

/** The status as the tabs word it ('On Hold' → 'On hold'); other text as written. */
export const projectStatusLabel = (status) => (
  PROJECT_STATUS_OPTIONS.find((option) => normalized(option.value) === normalized(status))?.label || cleanText(status)
);

/** Status as stored in the canonical spelling ('on hold' → 'On Hold'); other text as written. */
export const canonicalStatus = (status) => (
  PROJECT_STATUS_OPTIONS.find((option) => normalized(option.value) === normalized(status))?.value || cleanText(status)
);

/** Value used in filters for "the field is empty" (?engineer=none, kept from wave 1 deep links). */
export const NONE_VALUE = 'none';
export const OTHER_CITY = 'other';

/*
  Cities: the Location field is free text ("Abu Dhabi-UAE", "Dhafra, Abu Dhabi, UAE", "JVC Dubai",
  GPS coordinates). The City filter and the panel read a clean city from it; known districts map to
  their city. Al Ain is checked before Abu Dhabi ("Al Ain, Abu Dhabi" is Al Ain).
*/
export const CITY_OPTIONS = ['Abu Dhabi', 'Dubai', 'Al Ain', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain', 'Riyadh'];
const CITY_RULES = [
  ['Al Ain', /\bal[\s-]*ain\b/i],
  ['Abu Dhabi', /\babu[\s-]*dhabi\b|\bjubail\b|\bdhafra\b|\byas\b|\bsaadiyat\b|\breem\b|\bkhalifa city\b|\bmbz\b|\bmussafah\b|\bmasdar\b|\bal raha\b|\bal nahyan\b|\bmirfa\b|\bruwais\b/i],
  ['Dubai', /\bdubai\b|\bjvc\b|\bjlt\b|\bjumeirah\b|\bdeira\b|\bbusiness bay\b|\bal barsha\b|\bjebel ali\b|\bal furjan\b|\bmeydan\b/i],
  ['Sharjah', /\bsharjah\b/i],
  ['Ajman', /\bajman\b/i],
  ['Ras Al Khaimah', /\bras[\s-]*al[\s-]*khaimah\b|\brak\b/i],
  ['Fujairah', /\bfujairah\b/i],
  ['Umm Al Quwain', /\bumm[\s-]*al[\s-]*quwain\b/i],
  ['Riyadh', /\briyadh\b/i],
];

/** 'Dhafra, Abu Dhabi, UAE' → 'Abu Dhabi'; GPS / map links and unknown places → null. */
export function cityOf(location) {
  const place = describeLocation(location);
  if (!place || place.pin) return null;
  const match = CITY_RULES.find(([, pattern]) => pattern.test(place.text));
  return match ? match[0] : null;
}

/**
 * The project's clean city (03 #10): the one the server stores (from the location text, else the map pin,
 * else the name — backend/project_geo.py, same rules as cityOf), else read from the location here; '' if unknown.
 */
export function projectCity(project) {
  return cleanText(project?.city) || cityOf(project?.location) || '';
}

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Location split for the panel's City + Area fields:
 * 'Abu Dhabi - Al Jubail Island' → { city: 'Abu Dhabi', area: 'Al Jubail Island' };
 * 'JVC Dubai' → { city: 'Dubai', area: 'JVC' }; '25.10, 55.22' → { city: '', area: '25.10, 55.22' }.
 * The city name is only cut out of the text when the text ends with it, or starts with it followed by a
 * separator (',', '-', '/' …). A name that merely begins with the city stays whole as the area —
 * 'Dubai Hills Estate' → { city: 'Dubai', area: 'Dubai Hills Estate' } — so editing the area never
 * turns it into 'Hills Estate, Dubai'.
 */
export function splitLocation(location) {
  const text = cleanText(location);
  const city = cityOf(text) || '';
  if (!city) return { city: '', area: text };
  let area = text.replace(COUNTRY_SUFFIX, '').trim();
  const name = escapeRegExp(city).replace(/\s+/g, '[\\s-]*');
  const atEnd = new RegExp(`(^|[\\s,.\\-–]+)${name}[\\s,.]*$`, 'i');
  const atStart = new RegExp(`^\\s*${name}\\s*([,.\\-–/|:]+\\s*|$)`, 'i');
  if (atEnd.test(area)) area = area.replace(atEnd, '');
  else if (atStart.test(area)) area = area.replace(atStart, '');
  return { city, area: area.replace(EDGE_SEPARATORS, '').trim() };
}

/** City + Area back into the one Location field: 'Al Jubail Island, Abu Dhabi'. */
export const joinLocation = (area, city) => [cleanText(area), cleanText(city)].filter(Boolean).join(', ');

/* ---------- stage, due date and attention ---------- */

/** The server's stage_summary (GET /projects?stage_summary=true), or null. */
export const stageSummaryOf = (project) => (
  project?.stage_summary && typeof project.stage_summary === 'object' ? project.stage_summary : null
);

/** Days past the end date (> 0) or left (< 0) for a project with a real end date; null otherwise. */
export const endDateDays = (project, { now } = {}) => ageDays(project?.end_date, { now });

/** Active and past its end date → days late; else 0. */
export function daysLate(project, { now } = {}) {
  if (!matchesStatusTab(project, 'Active')) return 0;
  const days = endDateDays(project, { now });
  return days !== null && days > 0 ? days : 0;
}

export const awaitingApprovalCount = (project) => Number(stageSummaryOf(project)?.pending_approval_count) || 0;
export const hasNoEngineer = (project) => matchesStatusTab(project, 'Active') && !cleanText(project?.project_engineer);

export const ATTENTION_OPTIONS = [
  { value: 'late', label: 'Past end date' },
  { value: 'awaiting', label: 'Awaiting approval' },
  { value: 'no-engineer', label: 'No Project Engineer' },
  { value: 'needs-setup', label: 'Needs setup' },
];

export function matchesAttention(project, attention, { now } = {}) {
  switch (attention) {
    case 'late': return daysLate(project, { now }) > 0;
    case 'awaiting': return awaitingApprovalCount(project) > 0;
    case 'no-engineer': return hasNoEngineer(project);
    case 'needs-setup': return missingSetup(project).length > 0;
    default: return true;
  }
}

/** Header shortcuts over the Active projects: late projects, stages awaiting approval, no engineer. */
export function attentionCounts(projects, { now } = {}) {
  const counts = { late: 0, awaitingStages: 0, awaitingProjects: 0, noEngineer: 0 };
  (projects || []).forEach((project) => {
    if (!matchesStatusTab(project, 'Active')) return;
    if (daysLate(project, { now }) > 0) counts.late += 1;
    const waiting = awaitingApprovalCount(project);
    counts.awaitingStages += waiting;
    if (waiting) counts.awaitingProjects += 1;
    if (hasNoEngineer(project)) counts.noEngineer += 1;
  });
  return counts;
}

/**
 * "Needs attention" order as a sortable tuple: Active first; within a status, late projects (most days
 * late first), then stages awaiting approval (longest wait first), then projects missing basics, then
 * the rest in the server's order (newest first, `index`).
 */
export function attentionKey(project, { now, index = 0 } = {}) {
  const late = daysLate(project, { now });
  const missing = missingSetup(project).length;
  let rank = 3;
  let weight = 0;
  if (late > 0) {
    rank = 0;
    weight = late;
  } else if (awaitingApprovalCount(project)) {
    rank = 1;
    weight = Number(stageSummaryOf(project)?.pending_approval_days) || 0;
  } else if (missing) {
    rank = 2;
    weight = missing;
  }
  return [statusOrder(project?.status), rank, -weight, index];
}

export const compareTuples = (a, b) => {
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
  }
  return 0;
};

export const SORT_OPTIONS = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'name:asc', label: 'Project A–Z' },
  { value: 'due:asc', label: 'Due date' },
  { value: 'stage:asc', label: 'Stage' },
  { value: 'status:asc', label: 'Status' },
  { value: 'updated:desc', label: 'Recently updated' },
];

// Both directions of every sortable column (a header click can give either one).
const SORT_LABELS = {
  name: { asc: 'Project A–Z', desc: 'Project Z–A' },
  due: { asc: 'Due date', desc: 'Due date, latest first' },
  stage: { asc: 'Stage', desc: 'Stage, furthest first' },
  status: { asc: 'Status', desc: 'Status, Completed first' },
  updated: { asc: 'Least recently updated', desc: 'Recently updated' },
};

/** The words for a ?sort= value: 'name:desc' → 'Project Z–A'; an unknown value → 'Custom'. */
export function sortLabel(value) {
  if (!value || value === 'attention') return 'Needs attention';
  const [key, dir = 'asc'] = String(value).split(':');
  return SORT_LABELS[key]?.[dir] || 'Custom';
}

// Money is never a sort key here (owner rule 13 Oct 2026: contract value and payment terms are for roles with
// Invoicing only) — a hand-made ?sort=total_project_value:desc must not order the list by a hidden amount.
const FINANCE_SORT_KEYS = new Set(['total_project_value', 'original_scope_value', 'payment_terms', 'quickbooks_customer']);

/** The value a column sorts by (empty values sort last in both directions). */
export function sortValue(project, key) {
  if (FINANCE_SORT_KEYS.has(key)) return null;
  switch (key) {
    case 'name': return `${cleanText(project?.name).replace(/\s+/g, ' ')} ${cleanText(project?.system_type)}`;
    case 'due': return toDateInputValue(project?.end_date) || null;
    case 'stage': {
      const summary = stageSummaryOf(project);
      if (!summary) return null;
      return summary.complete ? (Number(summary.count) || 0) + 1 : (Number(summary.current_index) || null);
    }
    case 'status': return statusOrder(project?.status);
    case 'updated': {
      const parsed = parseDateValue(project?.updated_at || project?.created_at);
      if (!parsed) return null;
      return parsed.dateOnly ? Date.UTC(parsed.y, parsed.m - 1, parsed.d) : parsed.date.getTime();
    }
    default: return project?.[key];
  }
}

const isBlankValue = (value) => value === null || value === undefined || value === '';

/** Sorts projects: sort = 'attention' | null, or { key, dir }. Ties keep the server's order. */
export function sortProjects(projects, sort, { now } = {}) {
  const list = (projects || []).map((project, index) => ({ project, index }));
  if (!sort || sort === 'attention') {
    return list
      .map((entry) => ({ ...entry, key: attentionKey(entry.project, { now, index: entry.index }) }))
      .sort((a, b) => compareTuples(a.key, b.key))
      .map((entry) => entry.project);
  }
  const factor = sort.dir === 'desc' ? -1 : 1;
  return list
    .map((entry) => ({ ...entry, value: sortValue(entry.project, sort.key) }))
    .sort((a, b) => {
      const aBlank = isBlankValue(a.value);
      const bBlank = isBlankValue(b.value);
      if (aBlank || bBlank) return aBlank === bBlank ? a.index - b.index : (aBlank ? 1 : -1);
      return (compareValues(a.value, b.value) * factor) || a.index - b.index;
    })
    .map((entry) => entry.project);
}

/* ---------- search + filters ---------- */

/** Every word must appear somewhere in the project (name, client, place, team, system, tags, scope, notes…). */
export function matchesSearch(project, query) {
  const words = normalized(query).split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const haystack = [
    project?.name, project?.client, project?.location, projectCity(project), project?.project_engineer,
    project?.senior_project_engineer, project?.system_type, project?.consultant, project?.contractor, project?.notes,
    ...(Array.isArray(project?.tags) ? project.tags : []), ...(Array.isArray(project?.scope) ? project.scope : []),
  ].map(normalized).join(' | ');
  return words.every((word) => haystack.includes(word));
}

/*
  The list filters (03 #5): key → how a project's value is read (`list` fields hold several values).
  NONE_VALUE ('none') = the field is empty — wave 1 links use ?engineer=none.
*/
export const FILTER_FIELDS = {
  system: { read: (p) => cleanText(p?.system_type) },
  engineer: { read: (p) => cleanText(p?.project_engineer) },
  // 03 #10: a project without a typed location still has a city when the server found one (pin, name).
  city: { read: (p) => projectCity(p) || (cleanText(p?.location) ? OTHER_CITY : '') },
  client: { read: (p) => cleanText(p?.client) },
  scope: { read: (p) => (Array.isArray(p?.scope) ? p.scope.map(cleanText).filter(Boolean) : []), list: true },
  tag: { read: (p) => visibleTags(p?.tags), list: true },
  consultant: { read: (p) => cleanText(p?.consultant) },
  contractor: { read: (p) => cleanText(p?.contractor) },
};
export const FILTER_KEYS = Object.keys(FILTER_FIELDS);

function fieldValues(project, key) {
  const field = FILTER_FIELDS[key];
  const value = field.read(project);
  return field.list ? value : (value ? [value] : []);
}

function matchesFilter(project, key, wanted) {
  if (!Array.isArray(wanted) || !wanted.length) return true;
  const values = fieldValues(project, key).map((item) => item.toLowerCase());
  return wanted.some((item) => (item === NONE_VALUE ? !values.length : values.includes(String(item).toLowerCase())));
}

/** Every filter except the status tab. filters = { q, mine, attention, system: [], engineer: [], … }. */
export function filterProjects(projects, filters = {}, { myName, now } = {}) {
  return (projects || []).filter((project) => {
    if (filters.mine && !isMyProject(project, myName)) return false;
    if (!matchesSearch(project, filters.q)) return false;
    if (filters.attention && !matchesAttention(project, filters.attention, { now })) return false;
    return FILTER_KEYS.every((key) => matchesFilter(project, key, filters[key]));
  });
}

/**
 * Chip options with counts — [{ value, label, count }] by name, "Other places" last, plus a muted
 * "No …" row (value 'none') when some projects have the field empty. `extra` adds values with 0 projects
 * (e.g. every engineer from Admin › Users).
 */
export function filterOptions(projects, key, { emptyLabel, extra = [] } = {}) {
  const counts = new Map();
  let empty = 0;
  (projects || []).forEach((project) => {
    const values = fieldValues(project, key);
    if (!values.length) empty += 1;
    values.forEach((item) => {
      const id = item.toLowerCase();
      const entry = counts.get(id) || { value: item, label: item === OTHER_CITY ? 'Other places' : item, count: 0 };
      entry.count += 1;
      counts.set(id, entry);
    });
  });
  extra.map(cleanText).filter(Boolean).forEach((item) => {
    if (!counts.has(item.toLowerCase())) counts.set(item.toLowerCase(), { value: item, label: item, count: 0 });
  });
  const options = [...counts.values()].sort((a, b) => {
    if (a.value === OTHER_CITY || b.value === OTHER_CITY) return a.value === OTHER_CITY ? 1 : -1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
  if (emptyLabel && empty) options.push({ value: NONE_VALUE, label: emptyLabel, count: empty, muted: true });
  return options;
}

/* ---------- sites: the systems of one site (same name + client) (03 #7) ---------- */

/*
  Since project names are unique (BUG-009) a site's next system gets its own name, "<site> — <system>"
  (systemProjectName below: what the New / Duplicate panel offers). The site part is what groups it with the site's
  other systems — in the list's site rows, the site count, the map's places and "Use the pin of …".
*/
const SYSTEM_SUFFIX = ' — ';

/**
 * 'Nobu Hotel — HA KNX' → 'Nobu Hotel' when the part after the last " — " is a system type (one of the chips, or
 * the project's own `systemType`); any other name is returned whole (single spaces, no invisible characters).
 */
export function siteBaseName(name, systemType) {
  const text = nameWords(name);
  const at = text.lastIndexOf(SYSTEM_SUFFIX);
  if (at <= 0) return text;
  const suffix = text.slice(at + SYSTEM_SUFFIX.length).toLowerCase();
  const systems = [...SYSTEM_TYPE_CHOICES, systemType].map((item) => nameWords(item).toLowerCase()).filter(Boolean);
  return systems.includes(suffix) ? text.slice(0, at).trim() : text;
}

/** 'ARCO Majlis 1 ( MBZ)', 'arco majlis 1 (MBZ)' and 'ARCO Majlis 1 (MBZ) — BMS' are the same site name. */
export const siteNameKey = (name, systemType) => siteBaseName(name, systemType).toLowerCase()
  .replace(/\(\s+/g, '(').replace(/\s+\)/g, ')');

/** A project's site key (its name without a "— <its system>" suffix). */
export const siteKeyOf = (project) => siteNameKey(project?.name, project?.system_type);

/** The name a site row shows: a system's plain name ('Nobu Hotel'), else the first one's site part. */
function siteDisplayName(projects) {
  const plain = projects.find((project) => cleanText(project.name)
    && siteBaseName(project.name, project.system_type) === nameWords(project.name));
  return cleanText(plain?.name) || siteBaseName(projects[0]?.name, projects[0]?.system_type) || 'Untitled project';
}

/**
 * Groups the (already sorted) projects by site — same site name (siteKeyOf: 'Nobu Hotel — HA KNX' is a system of
 * 'Nobu Hotel') and client. A project without a client joins its name's group when that name has exactly one
 * client. Returns entries in the order of each site's
 * first project: { kind: 'project', project } or { kind: 'group', key, name, client, projects } (2+).
 */
export function groupBySite(projects) {
  const list = projects || [];
  const clientsByName = new Map();
  list.forEach((project) => {
    const nameKey = siteKeyOf(project) || `#${project?.id}`;
    if (!clientsByName.has(nameKey)) clientsByName.set(nameKey, new Set());
    const client = normalized(project?.client);
    if (client) clientsByName.get(nameKey).add(client);
  });
  const entries = [];
  const bySite = new Map();
  list.forEach((project) => {
    const nameKey = siteKeyOf(project) || `#${project?.id}`;
    const clients = clientsByName.get(nameKey);
    const client = normalized(project?.client) || (clients.size === 1 ? [...clients][0] : '');
    const siteKey = `${nameKey}|${client}`;
    if (bySite.has(siteKey)) {
      bySite.get(siteKey).projects.push(project);
      return;
    }
    const entry = { kind: 'group', key: `site:${siteKey}`, projects: [project] };
    bySite.set(siteKey, entry);
    entries.push(entry);
  });
  return entries.map((entry) => {
    if (entry.projects.length < 2) return { kind: 'project', project: entry.projects[0] };
    return {
      ...entry,
      name: siteDisplayName(entry.projects),
      client: entry.projects.map((p) => cleanText(p.client)).find(Boolean) || '',
    };
  });
}

/** Number of distinct sites (groups + single projects) — "45 projects · 38 sites". */
export const countSites = (projects) => groupBySite(projects).length;

/** What a group row says about its projects. */
export function describeGroup(group, { now } = {}) {
  const projects = group.projects || [];
  const statuses = [...new Set(projects.map((p) => canonicalStatus(p.status)).filter(Boolean))];
  const place = projects.map((p) => describeLocation(p.location)).find((item) => item && !item.pin);
  const open = projects.filter((p) => stageSummaryOf(p) && !stageSummaryOf(p).complete);
  const earliest = open.length
    ? open.reduce((best, p) => (Number(stageSummaryOf(p).current_index) < Number(stageSummaryOf(best).current_index) ? p : best))
    : null;
  const waiting = projects.filter((p) => awaitingApprovalCount(p));
  const longestWait = waiting.length
    ? waiting.reduce((best, p) => ((Number(stageSummaryOf(p).pending_approval_days) || 0) > (Number(stageSummaryOf(best).pending_approval_days) || 0) ? p : best))
    : null;
  const dated = projects.filter((p) => sortValue(p, 'due'));
  const soonest = dated.length ? dated.reduce((best, p) => (sortValue(p, 'due') < sortValue(best, 'due') ? p : best)) : null;
  return {
    count: projects.length,
    engineers: [...new Set(projects.map((p) => cleanText(p.project_engineer)).filter(Boolean))],
    unassigned: projects.filter((p) => !cleanText(p.project_engineer)).length,
    missingClient: group.client ? projects.filter((p) => !cleanText(p.client)).length : 0,
    status: statuses.length === 1 ? statuses[0] : null,
    placeText: place ? place.text : '',
    city: projects.map((p) => projectCity(p)).find(Boolean) || null,
    earliest,
    longestWait,
    soonest,
    late: projects.reduce((max, p) => Math.max(max, daysLate(p, { now })), 0),
  };
}

/* ---------- the New / Edit project panel (03 #8) ---------- */

export const SYSTEM_TYPE_CHOICES = ['BMS', 'LCS', 'HA KNX', 'HA ZigB', 'EMS', 'GRMS'];
export const OTHER_SYSTEM = 'other';

/** The chip a stored system type selects: 'ha zigb' → 'HA ZigB'; any other text → 'other' (text kept). */
export function systemChoiceOf(systemType) {
  const text = cleanText(systemType);
  if (!text) return '';
  return SYSTEM_TYPE_CHOICES.find((choice) => normalized(choice) === normalized(text)) || OTHER_SYSTEM;
}

/** Other projects with the same site name ("4 projects named Nobu Hotel already exist"). */
export function sameNameProjects(projects, name, { excludeId } = {}) {
  const key = siteNameKey(name);
  if (!key) return [];
  return (projects || []).filter((project) => project.id !== excludeId && siteNameKey(project.name) === key);
}

/*
  Project names are unique (BUG-009, owner decision 2026-10-04). The server refuses creating a project, or renaming
  one, to a name another project has — compared like backend/project_name_rule.py name_key: NFKC, without invisible
  characters, trimmed, runs of spaces as one, ignoring case. Older duplicates keep working: an edit is only checked
  when its name really changes. Strict on purpose: siteNameKey above (grouping) also folds "( MBZ)" into "(MBZ)" and
  drops a "— <system>" suffix; the server does neither, so it is not used here.
*/
// toUpperCase first: 'Straße' and 'STRASSE' fold alike, as Python's casefold() does on the server.
export const projectNameKey = (name) => nameWords(name).toUpperCase().toLowerCase();

/** The server's 400 for a taken name, word for word (shown in the same place whichever side noticed it). */
export const projectNameTakenMessage = (name) => (
  `A project named "${nameWords(name)}" already exists — open it or choose another name`
);
export const isProjectNameTakenMessage = (message) => /^A project named ".*" already exists\b/.test(cleanText(message));

/** The other projects that already use exactly this name (the server's comparison). */
export function projectsNamed(projects, name, { excludeId } = {}) {
  const key = projectNameKey(name);
  if (!key) return [];
  return (projects || []).filter((project) => project && project.id !== excludeId && projectNameKey(project.name) === key);
}

/**
 * The projects that block the panel's name ([] = free). New and duplicate always check; an edit only when the
 * name changes beyond case / spacing, so an older duplicate still saves its other fields.
 */
export function nameTakenBy(projects, form, { mode = 'create', initial = EMPTY_PROJECT_FORM, projectId } = {}) {
  if (mode === 'edit' && projectNameKey(form?.name) === projectNameKey(initial?.name)) return [];
  return projectsNamed(projects, form?.name, { excludeId: mode === 'edit' ? projectId : undefined });
}

/**
 * "Nobu Hotel — HA KNX": a free name for the same site's other system (offered when the name is taken). Built on the
 * site part, so a copy of "Nobu Hotel — HA KNX" for LCS is "Nobu Hotel — LCS"; siteNameKey groups it with the site.
 */
export function systemProjectName(name, systemType) {
  const base = siteBaseName(name);
  const system = nameWords(systemType);
  if (!base || !system || projectNameKey(base).endsWith(` ${projectNameKey(system)}`)) return '';
  return `${base}${SYSTEM_SUFFIX}${system}`;
}

/** The live check under the Waze link: { tone, text, pin? } — null for an empty field. */
export function wazeLinkStatus(value) {
  const text = cleanText(value);
  if (!text) return null;
  if (!URL_PATTERN.test(text)) return { tone: 'red', text: 'Paste the whole link, starting with https://' };
  // The server's reading (pinLink.js = project_geo.pin_from_link): a directions link gives its destination.
  const pin = pinFromLink(text);
  if (!pin) return { tone: 'amber', text: 'No map pin in this link — the project will not show on the map' };
  return { tone: 'green', text: `Pin found · ${pin.lat.toFixed(4)}, ${pin.lng.toFixed(4)}`, pin };
}

/**
 * Field errors of the panel: { name, system_type, end_date, waze_url }. `initial` = the form it opened
 * with: the Waze check only runs once that field was changed, so an old free-text link never blocks
 * saving the project's other fields. A system type is required for new projects only. With `projects`
 * (the loaded list) a name another project already has is an error too (BUG-009, see nameTakenBy).
 */
export function validateProjectForm(form, { mode = 'create', initial = EMPTY_PROJECT_FORM, projects = null, projectId } = {}) {
  const errors = {};
  if (!cleanText(form.name)) errors.name = 'Enter the project name';
  else if (projects) {
    const taken = nameTakenBy(projects, form, { mode, initial, projectId });
    if (taken.length) errors.name = projectNameTakenMessage(taken[0].name);
  }
  if (mode !== 'edit' && !cleanText(form.system_type)) errors.system_type = 'Pick the system type';
  const start = cleanText(form.start_date);
  const end = cleanText(form.end_date);
  if (start && end && end < start) errors.end_date = "End date can't be before the start date";
  if (cleanText(form.waze_url) !== cleanText(initial.waze_url)) {
    const waze = wazeLinkStatus(form.waze_url);
    if (waze?.tone === 'red') errors.waze_url = waze.text;
  }
  return errors;
}

/** The panel's form differs from what it opened with (drives the unsaved-changes guard). */
export function formChanged(form, initial) {
  const comparable = (value) => JSON.stringify({
    ...value,
    custom_fields: normalizeProjectCustomFields(value.custom_fields).map(({ title, content }) => ({ title, content })),
  });
  return comparable(form) !== comparable(initial);
}

/** Scope choices: the defaults, every scope used on a project, and what this form already has. */
export function scopeOptions(projects, current = []) {
  const seen = new Map();
  [...DEFAULT_SCOPE_OPTIONS, ...(projects || []).flatMap((p) => (Array.isArray(p.scope) ? p.scope : [])), ...current]
    .map(cleanText)
    .filter(Boolean)
    .forEach((item) => { if (!seen.has(item.toLowerCase())) seen.set(item.toLowerCase(), item); });
  return [...seen.values()];
}
